<script setup lang="ts">
import Map from '@corporate/components/Map/Map.vue'
import { useDomain } from '@corporate/composables'
import type {
  ContactInformationGridApiModel,
  OpeningHoursSectionApiModel,
  ContactSectionApiModel,
  ContentSectionApiModel,
} from '@gro-up/api/corporate'
import { ContactComponent } from '@gro-up/ui/components'
import { computed } from 'vue'

import type { ContactAddressProps } from '../../../../../packages/ui/src/components/ContactComponent/ContactAddress.vue';

import { useRuntimeConfig } from '#app'

const props = defineProps<ContactInformationGridApiModel>()

const domain = useDomain()
const runtimeConfig = useRuntimeConfig()

const mapboxAccessToken = runtimeConfig.public.mapboxAccessToken as string

const hasCoordinates = computed(() => {
  const { latitude, longitude } = props.coordinates ?? {}

  return latitude != null && longitude != null && !(latitude === 0 && longitude === 0)
})

// Transform the API data to match ContactComponent props
const transformedProps = computed(() => {
  // Ensure blocks is always an array
  const blocks = props.blocks || []

  const centerCoords = [
    props.coordinates?.longitude ?? 0,
    props.coordinates?.latitude ?? 0
  ] as [number, number]


  if (!Array.isArray(blocks) || blocks.length === 0) {
    return {
      title: props.title || 'Contact',
      sections: [],
      mapView: {
        accessToken: mapboxAccessToken,
        initialCenter: centerCoords,
        initialZoom: 14,
        bounds: null,
      },
    }
  }

  const sections = blocks

    .map(block => {
      const contactBlock = block.contactBlock

      if (!contactBlock) {return null}

      switch (block.type) {
        case 'openingHours': {
          const openingBlock = contactBlock as OpeningHoursSectionApiModel

          if (!openingBlock.title || !openingBlock.openingTimes) {
            console.warn('openingHours block missing data:', block)

            return null
          }

          return {
            type: 'openingHours' as const,
            title: openingBlock.title,
            days: openingBlock.openingTimes.map(time => ({
              day: time.day,
              hours: time.hours,
            })),
          }
        }

        case 'contact': {
          const contactSectionBlock = contactBlock as ContactSectionApiModel

          if (!contactSectionBlock.title || !contactSectionBlock.content) {
            console.warn('contact block missing data:', block)

            return null
          }

          return {
            type: 'contact' as const,
            title: contactSectionBlock.title,
            content: contactSectionBlock.content,

            mapView: {
              initialCenter: [props.coordinates?.longitude ?? 0, props.coordinates?.latitude ?? 0] as [number, number],
              initialZoom: 14,
            },

            buttons:
              contactSectionBlock.buttons?.map(button => ({
                title: button.title,
                url: button.url,
                domain: button.domain,
                external: button.external,
                openInNewTab: button.openInNewTab,
                variant: button.variant,
              })) ?? [],
          }
        }

        case 'content': {
          const contentBlock = contactBlock as ContentSectionApiModel

          return {
            type: 'content' as const,
            title: contentBlock.title || 'Informatie',
            content: contentBlock.content,

            buttons:
              contentBlock.buttons?.map(button => ({
                title: button.title,
                url: button.url,
                domain: button.domain,
                external: button.external,
                openInNewTab: button.openInNewTab,
                variant: button.variant,
              })) ?? [],
          }
        }
        default:
          return null
      }
    })

    .filter(Boolean)

  return {
    title: props.title,
    sections: sections as ContactAddressProps[],
    mapView: {
      accessToken: mapboxAccessToken,
      initialCenter: centerCoords,
      initialZoom: 14,
      bounds: null,
    },
    domain: domain.value ?? undefined,
  }
})
</script>

<template>
  <ContactComponent v-bind="transformedProps">
    <template v-if="hasCoordinates" #map="{ locationMarker, mapView, domain: mapDomain }">
      <Map
        :access-token="mapView.accessToken"
        :initial-center="mapView.initialCenter"
        :initial-zoom="mapView.initialZoom"
        :bounds="mapView.bounds"
        :domain="mapDomain || 'head-office'"
        :locations="locationMarker ? [{
          id: 'contact-location',
          title: transformedProps.title,
          address: '', // Contact locations don't need address display
          position: {
            latitude: locationMarker.latitude,
            longitude: locationMarker.longitude
          },
          domain: mapDomain || 'head-office',
          url: ''
        }] : []"
        readonly />
    </template>
  </ContactComponent>
</template>
